import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MessageService } from '../../../services/message-service/message.service';
import { BetriebskontakteService } from './betriebskontakte.service';
import { Betriebskontakt } from './betriebskontakt';
import { BetriebskontakteBearbeitenDialogComponent } from './betriebskontakte-bearbeiten-dialog.component';
import {
    BetriebskontakteNeuerKontaktDialogComponent,
    NeuerKontaktDialogResult,
} from './betriebskontakte-neuer-kontakt-dialog.component';
import { BetriebskontakteErstellenDialogComponent } from './betriebskontakte-erstellen-dialog/betriebskontakte-erstellen-dialog.component';
import { LocalStorageService } from '../../../services/local-storage/local-storage.service';
import { KieneUtils } from '../../../utils/KieneUtils';

@Component({
    selector: 'kiene-betriebskontakte',
    templateUrl: './betriebskontakte.component.html',
    styleUrls: ['./betriebskontakte.component.scss'],
})
export class BetriebskontakteComponent implements OnInit, OnDestroy {
    @Input() showReturnButton: boolean = true;

    kontakte: Betriebskontakt[] = [];
    zeigeSuche = false;
    ladeKontakte = false;
    selectedFilter = 'all';
    searchControl = new FormControl('');
    private destroy$ = new Subject<void>();

    constructor(
        private betriebskontakteService: BetriebskontakteService,
        private messageService: MessageService,
        private dialog: MatDialog,
        private localStorageService: LocalStorageService,
    ) {}

    ngOnInit(): void {
        this.ladeKontakteList();

        // Suchfunktionalität
        this.searchControl.valueChanges
            .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe(() => {
                // Die getFilteredKontakte() Methode wird automatisch aufgerufen
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ladeKontakteList(): void {
        this.ladeKontakte = true;
        this.betriebskontakteService
            .getBetriebskontakte()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: response => {
                    this.kontakte = response.records;
                    this.ladeKontakte = false;
                },
                error: error => {
                    this.messageService.errorMessage('Fehler beim Laden der Kontakte', error);
                    this.ladeKontakte = false;
                },
            });
    }

    setFilter(filter: string): void {
        this.selectedFilter = filter;
    }

    getFilteredKontakte(): Betriebskontakt[] {
        // Erst nach Filter filtern
        let filteredKontakte: Betriebskontakt[];
        switch (this.selectedFilter) {
            case 'landwirt':
                filteredKontakte = this.kontakte.filter(k => k.betriebsart_id !== 9001 && k.betriebsart_id !== 9002);
                break;
            case 'haendler':
                filteredKontakte = this.kontakte.filter(k => k.betriebsart_id === 9002);
                break;
            case 'schlachthof':
                filteredKontakte = this.kontakte.filter(k => k.betriebsart_id === 9001);
                break;
            default:
                filteredKontakte = this.kontakte;
        }

        // Dann nach Suchbegriff filtern
        const searchTerm = this.searchControl.value?.toLowerCase().trim();
        if (searchTerm) {
            filteredKontakte = filteredKontakte.filter(
                kontakt =>
                    kontakt.bezeichnung_final?.toLowerCase().includes(searchTerm) ||
                    kontakt.zusatz_final?.toLowerCase().includes(searchTerm) ||
                    kontakt.vvvo?.includes(searchTerm) ||
                    kontakt.ort?.toLowerCase().includes(searchTerm) ||
                    kontakt.strasse?.toLowerCase().includes(searchTerm),
            );
        }

        return filteredKontakte;
    }

    /**
     * Prüft, ob ein Kontakt bereits in den eigenen Kontakten ist
     */
    istKontaktBereitsVorhanden(kontakt: Betriebskontakt): boolean {
        return this.kontakte.some(eigenerKontakt => KieneUtils.sameId(eigenerKontakt.kunde_id, kontakt.kunde_id));
    }

    getFilteredCount(filter: string): number {
        // Erst nach Filter filtern
        let filteredKontakte: Betriebskontakt[];
        switch (filter) {
            case 'landwirt':
                filteredKontakte = this.kontakte.filter(k => k.betriebsart_id !== 9001 && k.betriebsart_id !== 9002);
                break;
            case 'haendler':
                filteredKontakte = this.kontakte.filter(k => k.betriebsart_id === 9002);
                break;
            case 'schlachthof':
                filteredKontakte = this.kontakte.filter(k => k.betriebsart_id === 9001);
                break;
            default:
                filteredKontakte = this.kontakte;
        }

        // Dann nach Suchbegriff filtern
        const searchTerm = this.searchControl.value?.toLowerCase().trim();
        if (searchTerm) {
            filteredKontakte = filteredKontakte.filter(
                kontakt =>
                    kontakt.bezeichnung_final?.toLowerCase().includes(searchTerm) ||
                    kontakt.zusatz_final?.toLowerCase().includes(searchTerm) ||
                    kontakt.vvvo?.includes(searchTerm) ||
                    kontakt.ort?.toLowerCase().includes(searchTerm) ||
                    kontakt.strasse?.toLowerCase().includes(searchTerm),
            );
        }

        return filteredKontakte.length;
    }

    toggleSuche(): void {
        this.zeigeSuche = !this.zeigeSuche;
    }

    onKontaktHinzugefuegt(): void {
        this.ladeKontakteList();
    }

    onSchliesseSuche(): void {
        this.zeigeSuche = false;
    }

    oeffneNeuenKontaktDialog(): void {
        const dialogRef = this.dialog.open(BetriebskontakteNeuerKontaktDialogComponent, {
            width: '90vw',
            minWidth: '90vw',
            disableClose: true,
        });

        dialogRef.afterClosed().subscribe((result: NeuerKontaktDialogResult) => {
            if (result && result.success) {
                this.ladeKontakteList();
                if (result.openEdit && result.kontakt) {
                    this.onBearbeiten(result.kontakt);
                }
            }
        });
    }

    // Event-Handler für die Card-Komponente
    onBearbeiten(kontakt: Betriebskontakt): void {
        if (!this.canEditKontakt(kontakt)) {
            return;
        }
        const dialogRef = this.dialog.open(BetriebskontakteBearbeitenDialogComponent, {
            width: 'min(760px, 96vw)',
            maxWidth: '96vw',
            data: { kontakt },
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.ladeKontakteList();
            }
        });
    }

    canEditKontakt(kontakt: Betriebskontakt): boolean {
        const currentUserId = Number(this.localStorageService.getCurrentUser()?.benutzer_id ?? 0);
        const kontaktCreatorId = Number(kontakt?.erstellt_von ?? 0);
        const kontaktUserId = Number(kontakt?.benutzer_id ?? 0);
        return (
            currentUserId > 0 &&
            kontaktCreatorId > 0 &&
            kontaktUserId > 0 &&
            kontaktCreatorId === currentUserId &&
            kontaktUserId === currentUserId
        );
    }

    onToggleFavorit(kontakt: Betriebskontakt): void {
        this.betriebskontakteService
            .toggleFavorit(kontakt)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    const status = kontakt.favorit === 1 ? 'entfernt' : 'hinzugefügt';
                    this.messageService.infoMessage(`Favorit ${status}`);
                    this.ladeKontakteList();
                },
                error: error => {
                    this.messageService.errorMessage('Fehler beim Aktualisieren des Favoriten', error);
                },
            });
    }

    onEntfernen(kontakt: Betriebskontakt): void {
        if (confirm(`Möchten Sie den Kontakt "${kontakt.bezeichnung_final}" wirklich entfernen?`)) {
            this.betriebskontakteService
                .loescheBetriebskontakt(kontakt.kontakt_id!)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: () => {
                        this.messageService.infoMessage('Kontakt erfolgreich entfernt');
                        this.ladeKontakteList();
                    },
                    error: error => {
                        this.messageService.errorMessage('Fehler beim Entfernen des Kontakts', error);
                    },
                });
        }
    }

    onSendMail(kontakt: Betriebskontakt): void {
        if (kontakt.email) {
            const htmlAnchorElement = document.createElement('a');
            htmlAnchorElement.style.display = 'none';
            htmlAnchorElement.href = 'mailto:' + kontakt.email;
            document.body.appendChild(htmlAnchorElement);
            htmlAnchorElement.click();
            document.body.removeChild(htmlAnchorElement);
        }
    }

    onCallPhone(nummer: string): void {
        if (nummer) {
            const htmlAnchorElement = document.createElement('a');
            htmlAnchorElement.style.display = 'none';
            htmlAnchorElement.href = 'tel:' + nummer;
            document.body.appendChild(htmlAnchorElement);
            htmlAnchorElement.click();
            document.body.removeChild(htmlAnchorElement);
        }
    }

    goBack(): void {
        window.history.back();
    }
}
